import classNames from 'classnames';
import { useState } from 'react';
import {
  IDynamicRuleSetOptions,
  IGameRules,
} from '../../../Models/GameSetupModels';
import { SectionHeading } from '../StartScreen/StartScreen';
import {
  BUTTON_TEXT_CLASS_NAME,
  CARD_CLASS_NAME,
  FOCUS_CLASS_NAME,
} from '../StartScreen/styles';
import {
  SETTINGS_LIST_CLASS_NAME,
  StepperRow,
  SwitchRow,
} from './LobbyControls';
import { CUSTOM_RULESET, RULESETS, rulesSummary } from './rulesets';
import SpecialCards from './SpecialCards';

interface ILobbyRulesProps {
  rules: IGameRules;
  ruleset: string;
  onRulesetChange?: (ruleset: string) => void;
  onRulesChange?: (rules: IGameRules) => void;
}

type CardCountField =
  | 'numberOfHandCards'
  | 'numberOfVisibleCards'
  | 'numberOfHiddenCards';

type OptionField = {
  [K in keyof IDynamicRuleSetOptions]: IDynamicRuleSetOptions[K] extends boolean
    ? K
    : never;
}[keyof IDynamicRuleSetOptions];

const CARD_COUNT_SETTINGS: {
  field: CardCountField;
  title: string;
  label: string;
}[] = [
  { field: 'numberOfHandCards', title: 'Hand cards', label: 'hand cards' },
  {
    field: 'numberOfVisibleCards',
    title: 'Face-up table cards',
    label: 'face-up table cards',
  },
  {
    field: 'numberOfHiddenCards',
    title: 'Face-down table cards',
    label: 'face-down table cards',
  },
];

const OPTION_SETTINGS: {
  field: OptionField;
  title: string;
  description: string;
}[] = [
  {
    field: 'mustPlayIfCanPlay',
    title: 'Must play if you can',
    description: 'Pick up only when you have no play',
  },
  {
    field: 'mustShowHiddenFlip',
    title: 'Show failed flips',
    description: 'Everyone sees a face-down card that gets picked up',
  },
  {
    field: 'playAfterWinner',
    title: 'Play after a winner',
    description: 'Keep going until one player is left',
  },
  {
    field: 'canPickupVisibleCardsAsPartOfPlay',
    title: 'Pick up table cards',
    description: "Face-up cards you can't play go to your hand with the pile",
  },
  {
    field: 'playAgainAfterForcingAPickup',
    title: 'Play again after a pickup',
    description: 'Whoever forced the pickup goes again',
  },
];

interface IRulesetCardProps {
  name: string;
  summary: string;
}

export function RulesetCard({ name, summary }: IRulesetCardProps) {
  return (
    <div
      className={classNames(
        CARD_CLASS_NAME,
        'flex flex-col gap-[2px] p-[14px]'
      )}
    >
      <span className="text-[15px] font-semibold">{name}</span>
      <span className="text-[12px] font-medium text-ink-muted">{summary}</span>
    </div>
  );
}

interface IPlayRulesProps {
  labelledBy: string;
  options: IDynamicRuleSetOptions;
  onChange?: (options: IDynamicRuleSetOptions) => void;
}

export function PlayRules({ labelledBy, options, onChange }: IPlayRulesProps) {
  return (
    <ul aria-labelledby={labelledBy} className={SETTINGS_LIST_CLASS_NAME}>
      <StepperRow
        title="Cards in a row to burn"
        description="Burns when the same card is played this many times in a row"
        label="cards in a row to burn"
        value={options.numberInARowToBurn}
        min={2}
        onChange={
          onChange
            ? (numberInARowToBurn) =>
                onChange({ ...options, numberInARowToBurn })
            : undefined
        }
      />
      {OPTION_SETTINGS.map(({ field, title, description }) => (
        <SwitchRow
          key={field}
          title={title}
          description={description}
          checked={options[field]}
          onChange={
            onChange
              ? (checked) => onChange({ ...options, [field]: checked })
              : undefined
          }
        />
      ))}
    </ul>
  );
}

interface IRulesetOptionProps {
  name: string;
  summary: string;
  isSelected: boolean;
  onSelect: () => void;
}

function RulesetOption({
  name,
  summary,
  isSelected,
  onSelect,
}: IRulesetOptionProps) {
  return (
    <button
      role="radio"
      aria-checked={isSelected}
      onClick={onSelect}
      className={classNames(
        'flex w-full items-center gap-[12px] rounded-[16px] bg-surface p-[14px] text-ink shadow-soft transition-shadow duration-150',
        isSelected ? 'ring-2 ring-accent-ring' : 'ring-1 ring-hairline',
        FOCUS_CLASS_NAME
      )}
    >
      <span
        className={classNames(
          'flex min-w-0 flex-1 flex-col gap-[2px] text-left',
          BUTTON_TEXT_CLASS_NAME
        )}
      >
        <span className="text-[15px] font-semibold">{name}</span>
        <span className="text-[12px] font-medium text-ink-muted">
          {summary}
        </span>
      </span>
      <span
        aria-hidden="true"
        className={classNames(
          'flex size-[22px] shrink-0 items-center justify-center rounded-full transition-colors duration-150',
          isSelected ? 'bg-accent-ring' : 'ring-2 ring-ink/20 ring-inset'
        )}
      >
        {isSelected && <span className="size-[8px] rounded-full bg-white" />}
      </span>
    </button>
  );
}

export default function LobbyRules({
  rules,
  ruleset,
  onRulesetChange,
  onRulesChange,
}: ILobbyRulesProps) {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const options = rules.dynamicRuleSetOptions;

  const selectRuleset = (name: string) => {
    if (name === CUSTOM_RULESET) {
      setIsAdvancedOpen(true);
    }
    onRulesetChange?.(name);
  };

  const changeCardCount = onRulesChange
    ? (field: CardCountField) => (value: number) =>
        onRulesChange({ ...rules, [field]: value })
    : undefined;

  const changeOptions = onRulesChange
    ? (dynamicRuleSetOptions: IDynamicRuleSetOptions) =>
        onRulesChange({ ...rules, dynamicRuleSetOptions })
    : undefined;

  return (
    <section aria-labelledby="lobby-rules" className="flex flex-col gap-[8px]">
      <SectionHeading id="lobby-rules">Rules</SectionHeading>
      {onRulesetChange ? (
        <div
          role="radiogroup"
          aria-labelledby="lobby-rules"
          className="flex flex-col gap-[8px]"
        >
          {RULESETS.map(({ name, rules: presetRules }) => (
            <RulesetOption
              key={name}
              name={name}
              summary={rulesSummary(presetRules.dynamicRuleSetOptions)}
              isSelected={ruleset === name}
              onSelect={() => selectRuleset(name)}
            />
          ))}
          <RulesetOption
            name={CUSTOM_RULESET}
            summary={
              ruleset === CUSTOM_RULESET
                ? rulesSummary(options)
                : 'Set every option yourself'
            }
            isSelected={ruleset === CUSTOM_RULESET}
            onSelect={() => selectRuleset(CUSTOM_RULESET)}
          />
        </div>
      ) : (
        <RulesetCard name={ruleset} summary={rulesSummary(options)} />
      )}
      <button
        aria-expanded={isAdvancedOpen}
        aria-controls="lobby-advanced-rules"
        onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
        className={classNames(
          'flex h-[40px] items-center gap-[4px] self-start rounded-[12px] bg-transparent px-[4px] text-[14px] font-semibold text-ink-muted',
          FOCUS_CLASS_NAME
        )}
      >
        <span className={BUTTON_TEXT_CLASS_NAME}>Advanced</span>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={classNames('transition-transform duration-150', {
            'rotate-180': isAdvancedOpen,
          })}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {isAdvancedOpen && (
        <div id="lobby-advanced-rules" className="flex flex-col gap-[16px]">
          <div className="flex flex-col gap-[8px]">
            <SectionHeading id="lobby-deal">Deal</SectionHeading>
            <ul
              aria-labelledby="lobby-deal"
              className={SETTINGS_LIST_CLASS_NAME}
            >
              {CARD_COUNT_SETTINGS.map(({ field, title, label }) => (
                <StepperRow
                  key={field}
                  title={title}
                  label={label}
                  value={rules[field]}
                  onChange={changeCardCount?.(field)}
                />
              ))}
            </ul>
          </div>
          <div className="flex flex-col gap-[8px]">
            <SectionHeading id="lobby-special-cards">
              Special cards
            </SectionHeading>
            <SpecialCards
              labelledBy="lobby-special-cards"
              options={options}
              onChange={changeOptions}
            />
          </div>
          <div className="flex flex-col gap-[8px]">
            <SectionHeading id="lobby-play">Play</SectionHeading>
            <PlayRules
              labelledBy="lobby-play"
              options={options}
              onChange={changeOptions}
            />
          </div>
        </div>
      )}
    </section>
  );
}
